import {
  createColumnHelper,
  type AccessorFn,
  type AccessorFnColumnDef,
  type AccessorKeyColumnDef,
  type CellContext,
  type CellData,
  type ColumnDefTemplate,
  type GroupColumnDef,
  type RowData,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { type SimpleTableFeatures } from '../features.js';
import type {
  SimpleTableColumnDef,
  SimpleTableDisplayColumnDef,
  SimpleTableGroupColumnDef,
  SimpleTableRowData,
} from '../public.api.js';

/**
 * Type guard to determine if a given column definition is a `SimpleTableGroupColumnDef`.
 * @internal
 */
export function isSimpleTableGroupColumnDef<
  TData extends SimpleTableRowData,
  TValue = unknown,
>(columnDef: unknown): columnDef is SimpleTableGroupColumnDef<TData, TValue> {
  return (
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'columns' in columnDef &&
    Array.isArray((columnDef as { columns: Array<unknown> }).columns)
  );
}

/**
 * Type guard to determine if a given column definition is a `SimpleTableDisplayColumnDef`.
 */
function isSimpleTableDisplayColumnDef<
  TData extends SimpleTableRowData,
  TValue = unknown,
>(columnDef: unknown): columnDef is SimpleTableDisplayColumnDef<TData, TValue> {
  return (
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'accessor' in columnDef
  );
}

/**
 * Helper function to transform the public accessor key to the correct
 * member config for the tanstack/react-table ColumnDef.
 */
function splitAccessor<TData extends RowData, TValue extends CellData>(
  column: SimpleTableDisplayColumnDef<TData, TValue>,
): string | AccessorFn<TData, TValue> {
  if (typeof column.accessor === 'string') {
    return column.accessor;
  }

  if (typeof column.accessor === 'function') {
    return column.accessor;
  }

  return '';
}

/**
 * Maps the given header renderer to the internal header renderer and prevents
 * leaking.
 */
function createHeaderRenderer<TData extends RowData>(
  header: SimpleTableColumnDef<TData>['header'],
) {
  // We need to map the function from the outside here in order to not leak
  // tanstack table internals.
  if (typeof header === 'function') {
    return () => header();
  }
  return header;
}

const simpleTableCreateColumnHelper = createColumnHelper<
  SimpleTableFeatures,
  RowData
>();

/**
 * Prepares all exposed functions for the custom cell renderer.
 */
function createCellRenderer<TData extends SimpleTableRowData, TValue = unknown>(
  cell: SimpleTableDisplayColumnDef<TData, TValue>['cell'],
) {
  if (cell) {
    if (typeof cell === 'string') {
      return cell;
    }

    if (typeof cell === 'function') {
      const renderFn: ColumnDefTemplate<
        CellContext<SimpleTableFeatures, RowData, TValue>
      > = ({ getValue, row, column }) => {
        return cell({
          value: getValue(),
          rowIndex: row.index,
          rowData: row.original as TData,
          rowId: row.id,
        });
      };
      return renderFn;
    }

    return undefined;
  }
}

/**
 * Helper hook that will calculate which columns are enabled within the SimpleTable
 * @internal
 */
export function useColumns<
  TData extends SimpleTableRowData,
  TValue extends CellData,
>(propColumns: SimpleTableColumnDef<TData, TValue>[]) {
  return useMemo(() => {
    const createdColumns: Array<
      | GroupColumnDef<SimpleTableFeatures, RowData, unknown>
      | AccessorKeyColumnDef<SimpleTableFeatures, RowData, unknown>
      | AccessorFnColumnDef<SimpleTableFeatures, RowData, unknown>
    > = [];

    // Iterate through the columns and create the definitions required
    for (const firstLevelColumn of propColumns) {
      if (isSimpleTableGroupColumnDef(firstLevelColumn)) {
        const groupColumn = simpleTableCreateColumnHelper.group({
          id: firstLevelColumn.id,
          header: createHeaderRenderer(firstLevelColumn.header),
          meta: {
            width: firstLevelColumn.width,
            alignment: firstLevelColumn.alignment ?? 'left',
          },
          columns: firstLevelColumn.columns?.map((childColumn) => {
            const cellRenderer = createCellRenderer(childColumn.cell);
            return simpleTableCreateColumnHelper.accessor(
              splitAccessor(childColumn),
              {
                id: childColumn.id,
                header: createHeaderRenderer(childColumn.header),
                meta: {
                  width: childColumn.width,
                  minWidth: childColumn.minWidth,
                  maxWidth: childColumn.maxWidth,
                  alignment: childColumn.alignment ?? 'left',
                },
                ...(cellRenderer !== undefined && { cell: cellRenderer }),
              },
            );
          }),
        });

        createdColumns.push(groupColumn);
      } else if (isSimpleTableDisplayColumnDef(firstLevelColumn)) {
        const cellRenderer = createCellRenderer(firstLevelColumn.cell);
        createdColumns.push(
          simpleTableCreateColumnHelper.accessor(
            splitAccessor(firstLevelColumn),
            {
              id: firstLevelColumn.id,
              header: createHeaderRenderer(firstLevelColumn.header),
              meta: {
                width: firstLevelColumn.width,
                minWidth: firstLevelColumn.minWidth,
                maxWidth: firstLevelColumn.maxWidth,
                alignment: firstLevelColumn.alignment ?? 'left',
              },
              ...(cellRenderer !== undefined && { cell: cellRenderer }),
            },
          ),
        );
      }
    }

    return createdColumns;
  }, [propColumns]);
}
